import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Globe, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { CONTACT, SERVICES } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];
const VIEWPORT = { once: true, margin: "-80px" };

const ContactPattern = ({ testId = "contact-pattern", id = "contact-cad-grid" }) => (
  <svg aria-hidden="true" data-testid={testId} className="w-full h-full text-[#24589A]" viewBox="0 0 1200 700" preserveAspectRatio="none">
    <defs>
      <pattern id={id} width="72" height="72" patternUnits="userSpaceOnUse">
        <path d="M 72 0 L 0 0 0 72" fill="none" stroke="currentColor" strokeWidth="1" />
      </pattern>
    </defs>
    <rect width="1200" height="700" fill={`url(#${id})`} />
    <g fill="none" stroke="currentColor" strokeWidth="1.2">
      <path d="M120 560 H380 V420 H610 V560 H880 V380 H1080" />
      <path d="M180 170 H450 V290 H720 V170 H990" />
      <circle cx="450" cy="290" r="38" />
      <circle cx="990" cy="170" r="46" />
    </g>
  </svg>
);

const inputCls =
  "w-full rounded-md border border-[#E2E6E9] bg-[#F7F7F4] px-4 py-3.5 font-inter text-sm text-[#17232D] placeholder:text-[#8A96A3] focus:outline-none focus:border-[#F47721] focus:ring-2 focus:ring-[#F47721]/15 transition-[border-color,box-shadow] duration-300";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, service: form.service || undefined, phone: form.phone || undefined });
      toast.success("Enquiry sent. Our team will reach out shortly.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch {
      toast.error("Could not send your enquiry. Please try again or call us directly.");
    } finally {
      setSending(false);
    }
  };

  const details = [
    [MapPin, "Visit Us", CONTACT.address, "contact-address"],
    [Phone, "Call Us", CONTACT.phone, "contact-phone"],
    [Phone, "Also Reach Us", CONTACT.phone2, "contact-phone-2"],
    [Mail, "Email Us", CONTACT.email, "contact-email"],
    [Mail, "Also Email Us", CONTACT.email2, "contact-email-2"],
    [Globe, "Online", CONTACT.website, "contact-website"],
  ];

  return (
    <section id="contact" data-testid="contact-section" className="relative overflow-hidden bg-[#F7F7F4] py-24 lg:py-32">
      <motion.div
        aria-hidden="true"
        className="absolute inset-[-8%] opacity-[0.04] pointer-events-none"
        animate={{ x: [-22, 22, -22], y: [-8, 8, -8] }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
      >
        <ContactPattern />
      </motion.div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={VIEWPORT}
          transition={{ duration: 0.6, ease: EASE }}
          className="max-w-4xl"
        >
          <div className="flex items-center gap-4">
            <span className="h-[2px] w-10 bg-[#F47721]" />
            <p className="font-inter text-[11px] font-semibold uppercase tracking-[0.32em] text-[#F47721]" data-testid="contact-label">
              Start A Project
            </p>
          </div>
          <h2 className="font-manrope font-bold tracking-tight leading-[1.06] text-[42px] md:text-[52px] lg:text-[56px] mt-6" data-testid="contact-heading">
            <span className="block text-[#13263A]">DISCUSS YOUR PROJECT.</span>
            <span className="block text-[#F47721]">ENGINEER THE NEXT STEP.</span>
          </h2>
          <p className="font-inter text-[#59636D] text-base md:text-[17px] leading-[1.65] mt-6 max-w-2xl" data-testid="contact-description">
            Share your facility, design or project requirements and our team will respond with the right technical next steps.
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
          <motion.aside
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={VIEWPORT}
            transition={{ duration: 0.65, delay: 0.12, ease: EASE }}
            className="lg:col-span-5 self-start relative overflow-hidden rounded-[10px] bg-gradient-to-br from-[#13263A] to-[#0E2742] p-8 lg:p-10 text-white"
            data-testid="contact-details-panel"
          >
            <div aria-hidden="true" className="absolute inset-0 opacity-[0.08]">
              <ContactPattern testId="contact-panel-pattern" id="contact-panel-cad-grid" />
            </div>
            <div className="relative z-10">
              <p className="font-inter text-[11px] font-semibold uppercase tracking-[0.28em] text-[#F47721]">Contact Details</p>
              <div className="mt-8 space-y-7">
                {details.map(([Icon, label, value, tid], i) => (
                  <motion.div
                    key={label}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={VIEWPORT}
                    transition={{ duration: 0.5, delay: 0.25 + i * 0.07, ease: EASE }}
                    className="flex items-start gap-4"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-white/15 bg-white/[0.06] text-[#F47721]">
                      <Icon size={18} strokeWidth={1.5} />
                    </span>
                    <div>
                      <p className="font-inter text-[10px] font-semibold uppercase tracking-[0.2em] text-[#9AB3CC]">{label}</p>
                      <p data-testid={tid} className="font-inter text-sm md:text-[15px] mt-1.5 leading-relaxed font-medium text-white">{value}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.aside>

          <motion.form
            data-testid="contact-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={VIEWPORT}
            transition={{ duration: 0.65, delay: 0.2, ease: EASE }}
            className="lg:col-span-7 rounded-[10px] border border-[#E2E6E9] bg-white p-6 md:p-8 lg:p-10 shadow-[0_20px_60px_rgba(19,38,58,0.08)]"
          >
            <div className="flex items-center justify-between gap-4 border-b border-[#E5E8EA] pb-5">
              <div>
                <p className="font-inter text-[10px] font-semibold uppercase tracking-[0.22em] text-[#F47721]">Project Enquiry</p>
                <h3 className="font-manrope font-bold tracking-tight text-[#17232D] text-2xl mt-2">Tell us what you are planning.</h3>
              </div>
              <span className="hidden sm:block h-[2px] w-16 bg-[#F47721]" />
            </div>

            <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-5">
              <label className="block">
                <span className="mb-2 block font-inter text-[11px] font-semibold uppercase tracking-[0.14em] text-[#59636D]">Full Name *</span>
                <input data-testid="contact-name-input" required value={form.name} onChange={set("name")} className={inputCls} />
              </label>
              <label className="block">
                <span className="mb-2 block font-inter text-[11px] font-semibold uppercase tracking-[0.14em] text-[#59636D]">Email Address *</span>
                <input data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} className={inputCls} />
              </label>
              <label className="block">
                <span className="mb-2 block font-inter text-[11px] font-semibold uppercase tracking-[0.14em] text-[#59636D]">Phone Number</span>
                <input data-testid="contact-phone-input" type="tel" value={form.phone} onChange={set("phone")} className={inputCls} />
              </label>
              <label className="block">
                <span className="mb-2 block font-inter text-[11px] font-semibold uppercase tracking-[0.14em] text-[#59636D]">Service Of Interest</span>
                <select data-testid="contact-service-select" value={form.service} onChange={set("service")} className={`${inputCls} appearance-none`}>
                  <option value="">Select a service</option>
                  {SERVICES.map((s) => (
                    <option key={s.num} value={s.title}>{s.title}</option>
                  ))}
                </select>
              </label>
            </div>
            <label className="mt-5 block">
              <span className="mb-2 block font-inter text-[11px] font-semibold uppercase tracking-[0.14em] text-[#59636D]">Project Details *</span>
              <textarea
                data-testid="contact-message-input"
                required
                rows={6}
                placeholder="Facility type, location, scope, timelines or documentation requirements"
                value={form.message}
                onChange={set("message")}
                className={`${inputCls} resize-none`}
              />
            </label>
            <motion.button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="mt-7 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-md bg-[#F47721] px-8 py-4 font-inter text-[12px] font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-[#D96A1B] disabled:opacity-60"
            >
              {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={17} />}
              {sending ? "Sending…" : "Send Enquiry"}
            </motion.button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
